import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { guardarPreferencia, leerPreferencia } from '@/api/preferencias.api';
import { debounce } from '@/lib/debounce';
import { almacenDelNavegador, guardarLocal, leerLocal } from '@/lib/preferencias';
import { useAuthStore } from '@/store/auth.store';
import { useWorkspaceStore } from '@/store/workspace.store';

/**
 * Una preferencia de pantalla que sigue al usuario de la laptop al teléfono (tanda 6).
 *
 * Arranca con lo que haya en `localStorage` (la pantalla no parpadea esperando al servidor); cuando
 * llega la del servidor, esa gana y se copia al navegador. Al cambiar, el estado y `localStorage`
 * se actualizan al instante y el servidor se entera 500 ms después (el último gana). Si el guardado
 * falla no se le avisa a nadie ni se deshace nada: la próxima vez se vuelve a intentar.
 *
 * La copia local y la de la consulta son de CADA usuario: dos cuentas en el mismo navegador no
 * se pisan las vistas. Sin usuario cargado no se lee ni se escribe la copia de nadie.
 *
 * `sanear` es obligatorio: lo guardado puede ser de una versión vieja o estar roto.
 */
export function usePreferencia<T>(
  clave: string,
  porDefecto: T,
  sanear: (crudo: unknown) => T,
): [T, (nuevo: T | ((antes: T) => T)) => void] {
  const negocio = useWorkspaceStore((s) => s.activeId);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const queryClient = useQueryClient();
  const llave = ['preferencia', userId, clave, negocio] as const;

  const [valor, setValor] = useState<T>(() => leerLocal(userId, clave, sanear, porDefecto, almacenDelNavegador()));

  // Lo último que se sabe, para que `cambiar` y el efecto del servidor no lean un cierre viejo.
  const ultimo = useRef(valor);
  const saneRef = useRef(sanear);
  saneRef.current = sanear;
  const porDefectoRef = useRef(porDefecto);
  porDefectoRef.current = porDefecto;
  // Si la persona ya tocó algo, lo que llegue tarde del servidor no se lo pisa.
  const tocado = useRef(false);

  // Otra clave, otro negocio u otro usuario es otra preferencia: se arranca de nuevo con lo local de
  // ESA, y lo que llegue de su servidor vuelve a mandar. (La primera vez no hace falta: ya lo hizo el useState.)
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) {
      primera.current = false;
      return;
    }
    tocado.current = false;
    const local = leerLocal(userId, clave, saneRef.current, porDefectoRef.current, almacenDelNavegador());
    ultimo.current = local;
    setValor(local);
  }, [clave, negocio, userId]);

  const { data } = useQuery({
    queryKey: llave,
    queryFn: () => leerPreferencia<unknown>(clave),
    enabled: Boolean(negocio && userId),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data == null || tocado.current) return;
    const delServidor = saneRef.current(data);
    ultimo.current = delServidor;
    setValor(delServidor);
    guardarLocal(userId, clave, delServidor, almacenDelNavegador());
  }, [data, clave, userId]);

  // Un solo temporizador por montaje; al desmontar se manda lo pendiente.
  // El cliente HTTP manda el negocio activo EN EL MOMENTO del envío: si en esos 500 ms la persona
  // cambió de negocio (o de cuenta), el guardado iría al negocio o al usuario equivocado. Mejor
  // perderlo: queda en lo local de quien lo hizo.
  const mandar = useRef(
    debounce((c: string, v: unknown, delNegocio: string | null, delUsuario: string | null) => {
      if (useWorkspaceStore.getState().activeId !== delNegocio) return;
      if ((useAuthStore.getState().user?.id ?? null) !== delUsuario) return;
      guardarPreferencia(c, v).catch(() => undefined);
    }, 500),
  );
  useEffect(() => {
    const m = mandar.current;
    // Recargar o cerrar la pestaña no desmonta nada: sin esto, un cambio hecho en el último
    // medio segundo se perdía y al volver mandaba el valor viejo del servidor. `pagehide` es el
    // aviso que sí llega (también en el teléfono), y el guardado va con `keepalive` para que
    // el navegador lo termine aunque la página ya se haya ido.
    const alIrse = () => m.flush();
    window.addEventListener('pagehide', alIrse);
    return () => {
      window.removeEventListener('pagehide', alIrse);
      m.flush();
    };
  }, []);

  const cambiar = useCallback(
    (nuevo: T | ((antes: T) => T)) => {
      const siguiente = typeof nuevo === 'function' ? (nuevo as (antes: T) => T)(ultimo.current) : nuevo;
      tocado.current = true;
      ultimo.current = siguiente;
      setValor(siguiente);
      guardarLocal(userId, clave, siguiente, almacenDelNavegador());
      // La copia en caché también: al volver a montar la pantalla no debe resucitar el valor viejo.
      queryClient.setQueryData(['preferencia', userId, clave, negocio], siguiente);
      mandar.current(clave, siguiente, negocio, userId);
    },
    [clave, negocio, userId, queryClient],
  );

  return [valor, cambiar];
}
